import { useState } from 'react';
import { useNewcomerRewardPolicy, useMarketConfigs } from '../../features/settings';
import { RewardMarketSelect } from './RewardMarketSelect';
import { formatDateTime } from '../../lib/format';

function formatOptionalDateTime(value?: string): string {
  return value ? formatDateTime(value) : '-';
}

export function NewcomerRewardPage() {
  const markets = useMarketConfigs();
  const [selectedMarketId, selectMarket] = useState('CN');
  const market = markets.items.find((item) => item.market_id === selectedMarketId);
  const exponent = market?.currency_exponent ?? 2;
  const {
    data,
    form,
    loading,
    saving,
    error,
    setEnabled,
    setAmountYuan,
    save,
    reload,
  } = useNewcomerRewardPolicy(selectedMarketId, exponent);

  if (loading || markets.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error || markets.error) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-red-500 mb-3">{error || markets.error}</p>
        <button onClick={() => { reload(); markets.reload(); }} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <RewardMarketSelect markets={markets.items} value={selectedMarketId} disabled={saving} onChange={selectMarket} />
      <p className="text-sm text-gray-500">
        仅对所选地区 App 用户生效。功能开启期间新注册的账号会获得候选资格；关闭期间注册的账号不会补发。重新开启后，已有候选账号按最新金额继续自动判断发放。
      </p>

      <div className="space-y-5 rounded-md border border-gray-200 p-4">
        <label className="flex h-9 items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50"
          />
          开启新人注册奖励
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            奖励金额（{market?.currency}）
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={10 ** -exponent}
              step={10 ** -exponent}
              value={form.amountYuan}
              onChange={(e) => setAmountYuan(e.target.value)}
              className="h-9 w-60 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
            <span className="text-xs text-gray-400">{market?.currency}</span>
          </div>
          <p className="mt-1 text-xs text-gray-400">
            发放时按当前最新配置金额结算，每个账号最多发放一次。
          </p>
        </div>
      </div>

      <div>
        <button
          onClick={save}
          disabled={saving || !market}
          className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
        >
          {saving && (
            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
          保存
        </button>
      </div>

      {data && (
        <div className="pt-4 border-t border-gray-200 text-xs text-gray-400 space-y-1">
          <p>当前状态：{data.enabled ? '已开启' : '已关闭'}</p>
          <p>当前金额：{market?.currency_symbol}{(data.amount_fen / 10 ** exponent).toFixed(exponent)}</p>
          <p>最后更新：{formatOptionalDateTime(data.updated_at)}</p>
          {data.updated_by != null && <p>操作人 ID：{data.updated_by}</p>}
        </div>
      )}
    </div>
  );
}
